import { Component, OnInit } from '@angular/core';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { LogService } from '../../../../../services/log/log.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { UtilitiesService } from '../../../../../services/utils/utilities.service';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { AppContextService } from '../../../../../services/app-context.service';
import { User } from '../../../../../models/User/user';
import { OperationError } from '../../../../../models/server-result/operation-error';
import { SupplierRecordsUploadTemplateResult } from '../../../../../models/upload/suppliers/supplier-records-upload-template-result';
import { Observable, forkJoin } from 'rxjs';
import { ConfirmingContract } from '../../../../../models/contracts/confirming/confirming-contract';
import { ContractAndRelatedConnectionHelperService } from '../../../../../services/contract-related-connection/contract-related-connection-helper.service';
import { TranslateService } from '../../../../../services/translate/translate.service';

@Component({
	selector: 'eif-upload-suppliers-instructions-modal',
	templateUrl: './upload-suppliers-instructions-modal.component.html',
	styleUrls: ['./../upload-suppliers.scss'],
	standalone: false,
})
export class UploadSuppliersInstructionsModalComponent implements OnInit {
	public isPerformingRequest = false;
	public validationErrors: OperationError[] = [];
	public loggedUser: User | null = null;
	public showActiveAutomaticFundingRequestInfo: boolean = false;
	public requestErrors: OperationError[] = [];

	constructor(
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private utilitiesService: UtilitiesService,
		private appContext: AppContextService,
		private contractAndRelatedConnectionHelperService: ContractAndRelatedConnectionHelperService,
		private translateService: TranslateService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.determineIfActiveAutomaticFundingRequestInfoIsShown();
		});
	}

	public downloadTemplate() {
		const serviceCall = this.serverApiService.generateSupplierRecordsUploadTemplate();
		this.isPerformingRequest = true;
		serviceCall.subscribe({
			next: (operationResult: OperationResult<SupplierRecordsUploadTemplateResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidSupplierRecordsUploadTemplateResult(operationResult);
				} else {
					this.processInValidSupplierRecordsUploadTemplateResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[SuppliersUploadStep1Component::generateSupplierRecordsUploadTemplate] HttpErrorResponse: ', httpErrorResponse);

				this.toasterService.showError(
					'[SuppliersUploadStep1Component::generateSupplierRecordsUploadTemplate] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processInValidSupplierRecordsUploadTemplateResult(operationResult: OperationResult<SupplierRecordsUploadTemplateResult>) {
		let errors = operationResult.Errors;

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private processValidSupplierRecordsUploadTemplateResult(operationResult: OperationResult<SupplierRecordsUploadTemplateResult>) {
		const blob = this.utilitiesService.base64ToBlob(operationResult.Data.Content, operationResult.Data.ContentType);

		const element = document.createElement('a');
		element.href = URL.createObjectURL(blob);
		element.download = operationResult.Data.ContentName;
		document.body.appendChild(element);
		element.click();
	}

	public clickGotoUploadStep(): void {}

	private determineIfActiveAutomaticFundingRequestInfoIsShown(): boolean {
		if (this.loggedUser?.IsSystemAdmin) {
			return true;
		} else if (this.loggedUser?.IsBuyer && this.loggedUser?.IsCompanyAdmin) {
			let servicesCallsToMake: Array<Observable<OperationResult<ConfirmingContract[]>>> = [];

			for (let index = 0; index < this.loggedUser.Companies.length; index++) {
				let buyerCompany = this.loggedUser.Companies[index];

				if (!!buyerCompany?.Id) {
					servicesCallsToMake.push(this.serverApiService.getContractsForBuyer(buyerCompany.Id));
				}
			}

			const getAllRequests = forkJoin(servicesCallsToMake);

			this.isPerformingRequest = true;

			getAllRequests.subscribe((opResults) => {
				for (let index = 0; index < servicesCallsToMake.length; index++) {
					let getConfirmingContracts = opResults[index] as OperationResult<ConfirmingContract[]>;

					if (!getConfirmingContracts.IsValid) {
						let errors = getConfirmingContracts.Errors;
						if (errors && errors.length > 0) {
							this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
								errors: errors,
								autoClose: false,
							});
						}
					} else {
						if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
							for (let index = 0; index < getConfirmingContracts.Data.length; index++) {
								const contract = getConfirmingContracts.Data[index];

								if (this.contractAndRelatedConnectionHelperService.canActivateAutomaticFundindRequest(contract)) {
									this.showActiveAutomaticFundingRequestInfo = true;
									this.isPerformingRequest = false;

									break;
								}
							}

							if (this.showActiveAutomaticFundingRequestInfo) break;
						}
					}
				}

				this.isPerformingRequest = false;
			});
		}

		return false;
	}

	public start(): string {
		return 'Ok pressed';
	}
}
